import type { GSplatVisualShowcaseEffectOptions } from './GSplatVisualShowcaseEffect'
import { GSplatVisualShowcaseEffect } from './GSplatVisualShowcaseEffect'
import { SHOWCASE_SHADER_COMMON_GLSL, SHOWCASE_SHADER_COMMON_WGSL } from './showcaseShaderUtils'

const deepMeditationShaderGLSL = /* glsl */ `
uniform float uTime;
uniform float uTimeScale;
uniform float uIntensity;
uniform vec3 uCenter;
uniform float uEffectScale;

${SHOWCASE_SHADER_COMMON_GLSL}

void modifySplatCenter(inout vec3 center) {
  vec3 local = (center - uCenter) / uEffectScale;
  float t = uTime * uTimeScale;
  float radius = length(local.xy);
  float breathe = sin(t * 1.15 + radius * 3.4) * 0.5 + 0.5;
  float lift = sin(t * 0.72 + local.z * 1.8) * 0.035 * uIntensity;

  center.xy += normalize(local.xy + vec2(0.0001)) * (breathe - 0.5) * 0.07 * uIntensity * uEffectScale;
  center.z += lift * uEffectScale;
}

void modifySplatCovariance(
  vec3 originalCenter,
  vec3 modifiedCenter,
  inout vec3 covA,
  inout vec3 covB
) {
  float t = uTime * uTimeScale;
  float radius = length((originalCenter - uCenter).xy / uEffectScale);
  float breathe = sin(t * 1.15 + radius * 3.4) * 0.5 + 0.5;
  gsplatApplyUniformScale(covA, covB, 1.0 + (breathe - 0.5) * 0.35 * uIntensity);
}

void modifySplatColor(vec3 center, inout vec4 color) {
  vec3 local = (center - uCenter) / uEffectScale;
  float t = uTime * uTimeScale;
  float wave = sin(length(local.xy) * 4.0 - t * 1.8) * 0.5 + 0.5;
  vec3 calmA = vec3(0.33, 0.79, 0.93);
  vec3 calmB = vec3(0.96, 0.66, 0.42);
  color.rgb = mix(color.rgb, mix(calmA, calmB, wave), 0.34 * uIntensity);
}
`

const deepMeditationShaderWGSL = /* wgsl */ `
${SHOWCASE_SHADER_COMMON_WGSL}

fn modifySplatCenter(center: ptr<function, vec3f>) {
  let local = (*center - uniform.uCenter) / uniform.uEffectScale;
  let t = uniform.uTime * uniform.uTimeScale;
  let radius = length(local.xy);
  let breathe = sin(t * 1.15 + radius * 3.4) * 0.5 + 0.5;
  let lift = sin(t * 0.72 + local.z * 1.8) * 0.035 * uniform.uIntensity;
  let direction = normalize(local.xy + vec2f(0.0001));
  let lateral = direction * (breathe - 0.5) * 0.07 * uniform.uIntensity * uniform.uEffectScale;
  (*center) = vec3f(
    (*center).x + lateral.x,
    (*center).y + lateral.y,
    (*center).z + lift * uniform.uEffectScale
  );
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  let t = uniform.uTime * uniform.uTimeScale;
  let radius = length((originalCenter - uniform.uCenter).xy / uniform.uEffectScale);
  let breathe = sin(t * 1.15 + radius * 3.4) * 0.5 + 0.5;
  *scale = (*scale) * (1.0 + (breathe - 0.5) * 0.35 * uniform.uIntensity);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  let local = (center - uniform.uCenter) / uniform.uEffectScale;
  let t = uniform.uTime * uniform.uTimeScale;
  let wave = sin(length(local.xy) * 4.0 - t * 1.8) * 0.5 + 0.5;
  let calmA = vec3f(0.33, 0.79, 0.93);
  let calmB = vec3f(0.96, 0.66, 0.42);
  (*color) = vec4f(
    mix((*color).rgb, mix(calmA, calmB, wave), 0.34 * uniform.uIntensity),
    (*color).a
  );
}
`

export class GSplatVisualDeepMeditationEffect extends GSplatVisualShowcaseEffect {
  constructor(options: GSplatVisualShowcaseEffectOptions = {}) {
    super(
      {
        id: 'visual-deep-meditation',
        shader: deepMeditationShaderGLSL,
        shaderWGSL: deepMeditationShaderWGSL,
      },
      options
    )
  }
}
